import * as S from "sanctuary";

const getValue = ({
  someObject,
  type,
  sourceKey,
  isSubSchema,
  subSchema,
  subSchemaIsArray,
}) => {
  const parsedValue = S.get(S.is(type))(sourceKey)(someObject);
  if (!S.isJust(parsedValue) || !isSubSchema) {
    return parsedValue;
  }
  if (subSchemaIsArray) {
    return S.fromMaybe([])(parsedValue).map((subObject) =>
      toSchema(subSchema)(subObject)
    );
  }
  const schema = toSchema(subSchema)(parsedValue.value);
  return schema ? S.Just(schema) : S.Nothing;
};

// mutating for performance reasons
// parsing takes about twice as long to use R.assocs.
// same goes for transformValues.
// overall this is less performant than I would like for larger
// payloads, but is workable for now
const mapValues = (schemaFields) => (someObject) => {
  const result = {};
  schemaFields.forEach(
    ({ type, sourceKey, destKey, isSubSchema, subSchema, subSchemaIsArray }) => {
      result[destKey] = getValue({
        someObject,
        type,
        sourceKey,
        isSubSchema,
        subSchema,
        subSchemaIsArray,
      });
    }
  );
  return result;
};

// new guidelines specify that data from endpoints that return multiple records should
// come in as an array
// https://hardcore-poitras-bda9dd.netlify.app/en/responses/general-formatting.html
const mapArrayValues = (schemaFields) => (someArray) => {
  const result = [];
  someArray.map((someObject) => {
    const item = {};
    schemaFields.forEach(
      ({ type, sourceKey, destKey, isSubSchema, subSchema, subSchemaIsArray }) => {
        item[destKey] = getValue({
          someObject,
          type,
          sourceKey,
          isSubSchema,
          subSchema,
          subSchemaIsArray,
        });
      }
    );
    result.push(item);
  });
  return result;
};

const transformValues = (schemaTransformers) => (parsedSchema) => {
  schemaTransformers.forEach(({ key, func }) => {
    parsedSchema[key] = func(parsedSchema[key]);
  });
  return parsedSchema;
};

const toSchema = (schema) => (response) => {
  const mapped = mapValues(schema.fields)(response);
  return transformValues(schema.transformers)(mapped);
};

const arrayToSchema = (schema) => (response) => {
  const mapped = mapArrayValues(schema.fields)(response);
  return transformValues(schema.transformers)(mapped);
};

/**
 * Makes a deserializing function with typechecking.
 *
 * @example
 * const deserializeToSchema = (
 *   toSchema,
 *   "data",
 *   "no data object found In Response",
 *   (responseData) => typeof responseData === "object")
 * );
 * const deserializer = deserializeToSchema(USER);
 * const userData = deserializer(response);
 * @param {Function} schemaMethod - Method used to transform data values.
 * @param {String} key - Name of the key where data is stored in the response.
 * @param {String} typeErrorMessage - Error text for when data fails type checking.
 * @param {Function} typeEvaluator - Type checks response data and/or nested data.
 * @returns {Function} a function that can make a deserializer for a schema.
 */
const makeDeserializer =
  (schemaMethod, key, typeErrorMessage, typeEvaluator) =>
  /**
   * Takes a schema and creates a deserializer for that schema.
   * @param {Object} schema - A schema with fields and transformers that can be
   * used to deserialize response data.
   * @returns {Function} - A function that can deserialize data that matches the schema.
   */
  (schema) =>
  /**
   * Deserializing function that is returned by deserializeToSchema {@link deserializeToSchema}
   * Because of the nested data shape, deserializeToSchema has
   * a nested conditional structure.
   * @param {Object} response - Response data that will be deserialized.
   * Has shape:
   *   for a successful response (200-299): {data: {data: res}}
   *   for a successful response with no data: {data: {data: undefined}}
   *   for a non-successful response (not 200-299): {headers: {ok: false}}
   *   for an empty response: {data: undefined}: {}
   * @returns {(Object|Error)} - Deserialized response or an Error:
   *   for a successful response: Object
   *   for a successful response with no data: Error("no data object found In Response")
   *   for a non-successful response (not 200-299): Error(responseHeaders?.statusText || "No reason found")
   *   for an empty response: Error("no data found in response")
   */
  (response) => {
    // Headers should always exist on responses from client.js/makeRequest
    // Default exists for deserializing inside epics.
    const responseBody = response.data;
    const responseHeaders = response.headers ?? { ok: true };

    if (responseBody === undefined) {
      return S.Left(Error("no data found in response"));
    }

    if (responseHeaders.ok) {
      // Data is nested inside the response body
      // If the response body also holds metadata, deserializeMetadataToSchema is used instead.
      const responseData = responseBody[key];
      if (responseData === undefined || !typeEvaluator(responseData)) {
        return S.Left(Error(typeErrorMessage));
      }
      return S.Right(schemaMethod(schema)(responseData));
    }

    const error = new Error(responseHeaders?.statusText || "No reason found");
    error.status = responseHeaders.status;
    return S.Left(error);
  };

export const deserializeToSchema = makeDeserializer(
  toSchema,
  "data",
  "no data object found In Response",
  (responseData) => typeof responseData === "object"
);

export const deserializeArrayToSchema = makeDeserializer(
  arrayToSchema,
  "data",
  "no data array found In Response",
  (responseData) =>
    Array.isArray(responseData) &&
    responseData.every((item) => typeof item === "object")
);

export const deserializeMetadataToSchema = makeDeserializer(
  toSchema,
  "meta",
  "no metadata object found in response",
  (responseData) => typeof responseData === "object"
);
